import { AppStateMachineProvider } from '@/components/providers/app-state-machine-provider';
import { AppStateRedirectProvider } from '@/components/providers/app-state-redirect-provider';
import { IntercomProvider } from '@/components/providers/intercom-provider';
import { PosthogProvider } from '@/components/providers/posthog-provider';
import { SentryProvider } from '@/components/providers/sentry-provider';
import { TRPCProvider } from '@/components/providers/trpc-provider';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { CatalogProvider } from '@/features/catalog/components/global-catalog-provider';
import { CustomerModeProvider } from '@/features/customer-mode/hooks/use-customer-mode';
import { CLERK_PUBLISHABLE_KEY } from '@/lib/constants';
import { VoiceRecordingProvider } from '@/lib/contexts/voice-recording-context';
import { i18n } from '@/lib/i18n/i18n';
import { deDE } from '@clerk/localizations';
import { ClerkProvider } from '@clerk/react-router';
import type { ReactNode } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { I18nextProvider } from 'react-i18next';

if (!CLERK_PUBLISHABLE_KEY) {
    throw new Error('Missing Clerk Publishable Key');
}

type AppProvidersProps = {
    locale?: string;
    children?: ReactNode;
};

export function AppProviders({ children }: AppProvidersProps) {
    return (
        <HelmetProvider>
            <TRPCProvider>
                <ClerkProvider
                    publishableKey={CLERK_PUBLISHABLE_KEY}
                    appearance={{
                        variables: {
                            colorPrimary: '#103FEF',
                        },
                        elements: {
                            footer: 'hidden',
                        },
                    }}
                    localization={i18n.language === 'de' ? deDE : undefined}
                    signInUrl="/sign-in"
                >
                    <I18nextProvider i18n={i18n}>
                        <AppStateMachineProvider>
                            <AppStateRedirectProvider>
                                <PosthogProvider>
                                    <IntercomProvider>
                                        <SentryProvider>
                                            <CustomerModeProvider>
                                                <VoiceRecordingProvider>
                                                    <TooltipProvider>
                                                        <CatalogProvider>{children}</CatalogProvider>
                                                        <Toaster />
                                                    </TooltipProvider>
                                                </VoiceRecordingProvider>
                                            </CustomerModeProvider>
                                        </SentryProvider>
                                    </IntercomProvider>
                                </PosthogProvider>
                            </AppStateRedirectProvider>
                        </AppStateMachineProvider>
                    </I18nextProvider>
                </ClerkProvider>
            </TRPCProvider>
        </HelmetProvider>
    );
}
